import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CloudOff,
  Server,
  ShieldAlert,
  WifiOff,
} from 'lucide-react';
import type { IFeatitWellKnown } from '@featit/shared';
import { Button } from '@/components/Button';
import { AUTH_CARD_CLASS, AuthFrame } from '@/components/AuthFrame';
import { Input } from '@/components/Input';
import {
  deploymentAddressLabel,
  effectiveDeploymentAddress,
  forgetChosenDeployment,
  markDeploymentReached,
  readChosenDeploymentAddress,
  storeDeploymentAddress,
} from './deploymentAddress';
import {
  DEPLOYMENT_PROBE_OUTCOMES,
  probeDeployment,
  type DeploymentProbeOutcomeType,
  type IDeploymentProbeResult,
} from './deploymentProbe';
import { isDesktopRuntime } from '@/desktop/desktopDetection';
import { signedOutQuietLinkClasses } from '@/features/auth/signedOutChrome';
import {
  DEPLOYMENT_IDENTITY_FAILURES,
  deploymentIconUrl,
  readDeploymentIdentity,
  type DeploymentIdentityFailureType,
} from './deploymentIdentity';
import {
  FIRST_SCREEN_PATH,
  IDENTITY_FAILURE_DETAILS,
  IDENTITY_FAILURE_TITLES,
  currentFirstScreenTarget,
  signedOutEntryPath,
} from './firstScreen';
import { backNavigation } from '@/shell/backNavigation';
import { useT } from '@/i18n/LocaleProvider';
import type { MessageKey } from '@featit/shared/i18n/messages/index';
import { registerEnglishArea } from '@/i18n/catalogue';
import { onboarding } from '@featit/shared/i18n/messages/en/onboarding';

registerEnglishArea('onboarding', onboarding);

/**
 * *Use another FEATit server…*: the one screen that takes an address (F-0518, F-0333).
 *
 * On the desktop it is the first screen's secondary screen, one quiet link from Create account and
 * Sign in; an install that can reach nothing still lands here on its own. A typed address is checked
 * (`/api/health`), then asked who it is (`/.well-known/featit`), and its own name and icon are shown
 * with *Continue to <name>* — nothing is remembered until the person confirms (AC-FD3). An address
 * this install was already pointed at, arriving from the gate, still moves on by itself: that is app
 * data, not a guess.
 *
 * "Sign in, or point me at your deployment", as a screen rather than as an error.
 *
 * ADR-050 made every tenant a deployment and the desktop a shallow renderer of one, which means an
 * install that can reach nothing is now the ordinary first run rather than a broken one. It is
 * given the treatment a primary screen gets: it says what it is waiting for, names the address it
 * tried, and tells four different failures apart — nothing there, not ready yet, not ours, and not
 * an address — because each one has a different repair and a shared "something went wrong" sends
 * the reader to the wrong one.
 */

type ScreenState =
  | { kind: 'idle' }
  | { kind: 'checking'; address: string }
  | { kind: 'result'; result: IDeploymentProbeResult }
  | { kind: 'identity-failed'; address: string | null; failure: DeploymentIdentityFailureType }
  | { kind: 'verified'; address: string; identity: IFeatitWellKnown };

const IDENTITY_FAILURE_ICONS: Record<DeploymentIdentityFailureType, typeof AlertCircle> = {
  [DEPLOYMENT_IDENTITY_FAILURES.offline]: WifiOff,
  [DEPLOYMENT_IDENTITY_FAILURES.unreachable]: CloudOff,
  [DEPLOYMENT_IDENTITY_FAILURES.notFeatit]: AlertCircle,
  [DEPLOYMENT_IDENTITY_FAILURES.certificate]: ShieldAlert,
};

export interface IConnectDeploymentPageProps {
  /** Injected in tests; the real screen talks to the network. */
  probe?: typeof probeDeployment;
  /**
   * Choosing a different address changes what `API_BASE_URL` and the credential slots resolve to,
   * and both are read once at module load. Restarting is what makes the new answer take effect
   * everywhere at once, rather than leaving half the app addressing the old deployment.
   */
  restart?: (path: string) => void;
  /** Injected in tests; the real screen asks the deployment, natively on the desktop. */
  readIdentity?: typeof readDeploymentIdentity;
}

const FAILURE_TITLES: Record<DeploymentProbeOutcomeType, MessageKey> = {
  [DEPLOYMENT_PROBE_OUTCOMES.reachable]: 'onboarding.probe.connected',
  [DEPLOYMENT_PROBE_OUTCOMES.unreachable]: 'onboarding.probe.unreachable',
  [DEPLOYMENT_PROBE_OUTCOMES.unhealthy]: 'onboarding.probe.unhealthy',
  [DEPLOYMENT_PROBE_OUTCOMES.originRefused]: 'onboarding.probe.originRefused',
  [DEPLOYMENT_PROBE_OUTCOMES.notADeployment]: 'onboarding.probe.notADeployment',
  [DEPLOYMENT_PROBE_OUTCOMES.invalidAddress]: 'onboarding.probe.invalidAddress',
};

function defaultRestart(path: string) {
  window.location.assign(path);
}

export function ConnectDeploymentPage({
  probe = probeDeployment,
  restart = defaultRestart,
  readIdentity = readDeploymentIdentity,
}: IConnectDeploymentPageProps = {}) {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const chosenAddress = readChosenDeploymentAddress();
  const currentAddress = effectiveDeploymentAddress();

  const [address, setAddress] = useState(chosenAddress ?? '');
  const [state, setState] = useState<ScreenState>({ kind: 'idle' });
  const autoCheckedRef = useRef(false);
  // The first screen this screen is secondary to, when this install has one to go back to.
  const [firstScreen] = useState(currentFirstScreenTarget);
  const desktop = isDesktopRuntime();

  // F-0518: having chosen a server, a desktop lands on that server's first screen — Create account
  // and Sign in, for it — rather than straight on a sign-in form it may have no account for.
  const redirectTarget = (() => {
    const requested = params.get('redirect');
    if (!requested || !requested.startsWith('/') || requested.startsWith('//')) {
      return signedOutEntryPath();
    }
    return requested;
  })();

  const runProbe = useCallback(
    async (candidate: string) => {
      setState({ kind: 'checking', address: candidate });
      const result = await probe(candidate);
      setState({ kind: 'result', result });
      return result;
    },
    [probe],
  );

  /**
   * An address that is configured but has never answered is checked on arrival, not on a click.
   *
   * The reader was sent here by the gate precisely because nothing is known to work, so making them
   * press a button to discover that is asking them to do the app's job. Only on arrival from the
   * gate: somebody who came here deliberately wants to change the address, not to be redirected
   * away from the screen they opened.
   */
  useEffect(() => {
    if (autoCheckedRef.current) return;
    if (params.get('from') !== 'gate') return;
    if (!currentAddress) return;
    autoCheckedRef.current = true;
    void runProbe(currentAddress).then((result) => {
      if (result.outcome !== DEPLOYMENT_PROBE_OUTCOMES.reachable || !result.address) return;
      markDeploymentReached(result.address);
      navigate(redirectTarget, { replace: true });
    });
  }, [currentAddress, navigate, params, redirectTarget, runProbe]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const candidate = address.trim() || currentAddress;
    if (!candidate) {
      setState({
        kind: 'result',
        result: {
          outcome: DEPLOYMENT_PROBE_OUTCOMES.invalidAddress,
          address: null,
          detail: t('onboarding.connect.enter'),
        },
      });
      return;
    }

    // Offline is said without asking: a request that can only time out would hold the screen to
    // tell the person what their own device already knows (AC-FD6).
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setState({ kind: 'identity-failed', address: null, failure: 'offline' });
      return;
    }

    const result = await runProbe(candidate);
    if (result.outcome !== DEPLOYMENT_PROBE_OUTCOMES.reachable || !result.address) {
      /*
       * In a webview an untrusted certificate fails exactly like an absent host. On the desktop the
       * native check can tell them apart, so an "unreachable" is asked once more, natively, and a
       * certificate is named for what it is.
       */
      if (desktop && result.outcome === DEPLOYMENT_PROBE_OUTCOMES.unreachable && result.address) {
        const answer = await readIdentity(result.address);
        if (answer.outcome === DEPLOYMENT_IDENTITY_FAILURES.certificate) {
          setState({ kind: 'identity-failed', address: result.address, failure: answer.outcome });
        }
      }
      return;
    }

    // AC-FD3: who it is, before anything is remembered.
    const reached = result.address;
    setState({ kind: 'checking', address: reached });
    const answer = await readIdentity(reached);
    if (answer.outcome === 'verified') {
      setState({ kind: 'verified', address: reached, identity: answer.identity });
    } else {
      setState({ kind: 'identity-failed', address: reached, failure: answer.outcome });
    }
  }

  function handleContinue(address: string) {
    storeDeploymentAddress(address);
    markDeploymentReached(address);
    restart(redirectTarget);
  }

  /*
   * With a deployment chosen, the way off it is the cloud — the build's own deployment — when this
   * build has one (AC-FD4); a build with none can only forget the choice and ask again.
   */
  const cloudAddress = firstScreen && !firstScreen.isCloud ? firstScreen.cloudAddress : null;

  function handleForget() {
    forgetChosenDeployment();
    restart(cloudAddress ? signedOutEntryPath() : '/connect');
  }

  const checking = state.kind === 'checking';
  const result = state.kind === 'result' ? state.result : null;
  const verified = state.kind === 'verified' ? state : null;
  const identityFailure = state.kind === 'identity-failed' ? state.failure : null;
  const failure = result && result.outcome !== DEPLOYMENT_PROBE_OUTCOMES.reachable ? result : null;
  const IdentityFailureIcon = identityFailure ? IDENTITY_FAILURE_ICONS[identityFailure] : null;
  const verifiedIcon = verified ? deploymentIconUrl(verified.identity, verified.address) : null;
  const addressFieldError =
    failure?.outcome === DEPLOYMENT_PROBE_OUTCOMES.invalidAddress ? failure.detail : undefined;

  return (
    <AuthFrame
      width="panel"
      before={
        <>
          {/*
           * A desktop window has no browser back button, so the way back to Create account and Sign in
           * is on the page — whenever this install has a first screen to go back to.
           */}
          {desktop && firstScreen && (
            <p className="mb-2">
              <Link
                to={FIRST_SCREEN_PATH}
                state={backNavigation().state}
                className={signedOutQuietLinkClasses}
              >
                <ArrowLeft className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                {t('onboarding.common.back')}
              </Link>
            </p>
          )}
        </>
      }
      title={t('onboarding.connect.title')}
      subtitle={t('onboarding.connect.subtitle')}
    >
      <div className={AUTH_CARD_CLASS}>
        {identityFailure && IdentityFailureIcon && (
          <div
            role="alert"
            data-testid="connect-identity-failure"
            className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 dark:border-amber-500/30 dark:bg-amber-500/10"
          >
            <div className="flex items-start gap-2.5">
              <IdentityFailureIcon
                aria-hidden="true"
                className="mt-px h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300"
              />
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-amber-900 dark:text-amber-200">
                  {t(IDENTITY_FAILURE_TITLES[identityFailure])}
                </p>
                <p className="mt-1 text-[13px] leading-5 text-amber-800 dark:text-amber-200/90">
                  {t(IDENTITY_FAILURE_DETAILS[identityFailure])}
                </p>
              </div>
            </div>
          </div>
        )}

        {failure && failure.outcome !== DEPLOYMENT_PROBE_OUTCOMES.invalidAddress && (
          <div
            role="alert"
            className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 dark:border-amber-500/30 dark:bg-amber-500/10"
          >
            <div className="flex items-start gap-2.5">
              {failure.outcome === DEPLOYMENT_PROBE_OUTCOMES.unreachable ? (
                <CloudOff className="mt-px h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" />
              ) : (
                <AlertCircle className="mt-px h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" />
              )}
              <div>
                <p className="text-[13px] font-semibold text-amber-900 dark:text-amber-200">
                  {t(FAILURE_TITLES[failure.outcome])}
                </p>
                <p className="mt-1 text-[13px] leading-5 text-amber-800 dark:text-amber-200/90">
                  {failure.detail}
                </p>
                {failure.checks && failure.checks.length > 0 && (
                  <ul className="mt-2 space-y-1 text-[13px] leading-5 text-amber-800 dark:text-amber-200/90">
                    {failure.checks.map((check) => (
                      <li key={check} className="flex gap-2">
                        <span aria-hidden="true">•</span>
                        <span>{check}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {verified ? (
          <div className="text-center" data-testid="connect-verified">
            {verifiedIcon ? (
              <img
                src={verifiedIcon}
                alt=""
                className="mx-auto h-10 w-10 rounded-lg object-contain"
              />
            ) : (
              <Server
                aria-hidden="true"
                className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500"
              />
            )}
            <p className="mt-3 text-[15px] font-semibold text-gray-900 dark:text-gray-100">
              {verified.identity.name}
            </p>
            <p className="mt-1 text-[13px] leading-5 text-gray-500 dark:text-gray-400">
              {t('onboarding.connect.serverAt', {
                address: deploymentAddressLabel(verified.address),
              })}
            </p>
            <Button
              type="button"
              size="lg"
              className="mt-5 w-full"
              onClick={() => handleContinue(verified.address)}
            >
              {t('onboarding.connect.continueTo', { name: verified.identity.name })}
              <ArrowRight className="h-4 w-4" />
            </Button>
            <button
              type="button"
              onClick={() => setState({ kind: 'idle' })}
              className="mt-3 rounded text-[13px] font-medium text-gray-600 transition-colors hover:text-gray-900 evx-link-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:text-gray-300 dark:hover:text-white"
            >
              {t('onboarding.connect.differentAddress')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <Input
              id="deployment-address"
              name="deployment-address"
              label={t('onboarding.connect.serverAddress')}
              type="text"
              inputMode="url"
              spellCheck={false}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="url"
              placeholder={t('onboarding.connect.example')}
              hint={t('onboarding.connect.addressHint')}
              error={addressFieldError}
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              disabled={checking}
              autoFocus
            />
            {/*
             * "Try again" only after something was actually tried. A rejected address was never
             * sent anywhere, and the reader is correcting it rather than retrying it.
             */}
            <Button type="submit" size="lg" loading={checking} className="w-full">
              {(failure && failure.outcome !== DEPLOYMENT_PROBE_OUTCOMES.invalidAddress) ||
              identityFailure
                ? t('onboarding.common.tryAgain')
                : t('onboarding.connect.connect')}
            </Button>
          </form>
        )}

        {/*
         * One live region, announcing exactly what the screen says. A separate screen-reader-only
         * copy of this sentence would have it read twice, and the visible one is already the
         * right words.
         */}
        <div role="status" aria-live="polite">
          {checking && (
            // The button beside it is already spinning; a second spinner is noise, the sentence
            // is the information.
            <p className="mt-3 text-center text-[13px] text-gray-500 dark:text-gray-400">
              {t('onboarding.connect.reaching', {
                address: deploymentAddressLabel(state.address),
              })}
            </p>
          )}
        </div>
      </div>

      {chosenAddress && !verified && (
        /*
         * Stacked rather than a row: side by side, the sentence and the action together exceed
         * the card and wrap into a two-line layout with the button hanging off the left, which
         * reads as a broken row rather than a deliberate one. The address itself is already in
         * the field above, so what this adds is that the choice is remembered and how to undo it.
         */
        <div className="mt-4 rounded-lg border border-gray-200 bg-white px-3.5 py-3 dark:border-[#2a2a2a] dark:bg-[#171717]">
          <p className="flex items-start gap-2 text-[12px] leading-5 text-gray-500 dark:text-gray-400">
            <Server className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{t('onboarding.connect.remembered')}</span>
          </p>
          <button
            type="button"
            onClick={handleForget}
            className="ml-[22px] mt-1.5 rounded text-[12px] font-medium evx-link transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            {cloudAddress ? t('onboarding.connect.useCloud') : t('onboarding.connect.forget')}
          </button>
        </div>
      )}

      {!verified && !firstScreen && (
        <p className="mt-5 text-center text-[12px] leading-5 text-gray-500 dark:text-gray-400">
          {t('onboarding.connect.noServer')}
        </p>
      )}
    </AuthFrame>
  );
}
